import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import PageHeader from '../../components/ui/PageHeader';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import { MP_MAP } from '../../utils/productImage';
import {
  availableMetrics,
  fmtInt,
  fmtMetric,
  fmtMoney,
  isMoneyMetric,
  resolveCanFinance,
  seriesAt,
  seriesTotal,
} from './salesPlanMatrix';
import { ForecastMetadataChips, extractForecastMetadata } from './forecastMeta';

function fmtDate(s) {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  if (d.length !== 3) return String(s);
  return `${d[2]}.${d[1]}.${d[0]}`;
}

const STATUS_LABEL = { draft: 'Черновик', active: 'Активный', archived: 'Архивный' };
const STATUS_COLOR = { draft: 'default', active: 'success', archived: 'default' };
const FORECAST_MODE_LABEL = { simple: 'Простой', smart: 'Продвинутый', auto: 'Умный' };
const FORECAST_MODE_COLOR = { simple: 'default', smart: 'primary', auto: 'secondary' };
const GRANULARITY_LABEL = { day: 'День', week: 'Неделя', month: 'Месяц' };
const LEVEL_LABEL = { gruppa: 'Группа', podgruppa: 'Подгруппа', article: 'Товар', sku: 'SKU' };

// Подпись с разбивкой по МП из metricsObj.byMp (только штуки)
function mpBreakdown(byMp, field) {
  if (!byMp) return '';
  const parts = [];
  for (const code of ['1', '2', '3']) {
    const m = byMp[code];
    if (!m) continue;
    const v = Number(m[field]) || 0;
    if (!v) continue;
    parts.push(`${MP_MAP[code]}: ${fmtInt(v)}`);
  }
  return parts.join(' · ');
}

// Все доступные метрики для графика План vs Факт (timeseries)
const CHART_METRICS = [
  { key: 'qty', label: 'Штуки', plan: 'plan_qty', fact: 'fact_qty', money: false },
  { key: 'revenue', label: 'Сумма', plan: 'plan_revenue', fact: 'fact_revenue', money: true },
  { key: 'profit', label: 'Профит', plan: 'plan_profit', fact: 'fact_profit', money: true },
];

function dateKind(d) {
  if (d?.kind) return d.kind;
  if (d?.is_future_forecast) return 'forecast';
  return null;
}

function combineFactProjectedSeries(seriesBag, dates) {
  const fact = seriesBag?.fact;
  const forecast = seriesBag?.newForecast;
  const hasMetadata = dates.some((d) => dateKind(d));
  if (!hasMetadata || !fact || !forecast) return fact;

  const byDate = {};
  const total = { qty: 0, revenue: 0, profit: 0 };
  dates.forEach((d) => {
    const kind = dateKind(d);
    const factCell = fact.byDate?.[d.key] || {};
    const forecastCell = forecast.byDate?.[d.key] || {};
    const days = Number(d.fact_days || 0) + Number(d.forecast_days || 0);
    const forecastPart = kind === 'forecast' ? 1 : (kind === 'mixed' && days > 0 ? Number(d.forecast_days || 0) / days : 0);
    const cell = {
      qty: Number(factCell.qty || 0) + Number(forecastCell.qty || 0) * forecastPart,
      revenue: Number(factCell.revenue || 0) + Number(forecastCell.revenue || 0) * forecastPart,
      profit: Number(factCell.profit || 0) + Number(forecastCell.profit || 0) * forecastPart,
    };
    byDate[d.key] = cell;
    total.qty += cell.qty;
    total.revenue += cell.revenue;
    total.profit += cell.profit;
  });
  return { byDate, total };
}

function displaySeries(seriesBag, key, dates) {
  if (key !== 'factProjected') return seriesBag?.[key];
  return seriesBag?.factProjected || combineFactProjectedSeries(seriesBag, dates);
}

// Вложенные датасеты позиции: План / Факт с прогнозом будущих дат.
// `color` — цвет подписи показателя, `tone` — палитра фона строки (мягкий alpha-фон).
// План/Факт переиспользуют цвета легенды графика «План vs Факт» (primary/success).
const SERIES_ROWS = [
  { key: 'plan', label: 'План', color: 'primary.main', tone: 'primary', strong: false },
  { key: 'factProjected', label: 'Факт', color: 'success.main', tone: 'success', strong: false },
];

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function safeFilePart(value) {
  return String(value || 'план')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'план';
}

function excelNumber(value) {
  const n = Number(value) || 0;
  return Math.round(n * 100) / 100;
}

function appendPlanExportRows(out, node, dates, exportMetrics, parentPath = []) {
  const name = node.name || node.key || 'Без названия';
  const path = [...parentPath, name];
  const base = {
    Уровень: LEVEL_LABEL[node.level] || node.level || '',
    Путь: path.join(' / '),
    Позиция: name,
  };

  exportMetrics.forEach((metricDef) => {
    SERIES_ROWS.forEach((seriesDef) => {
      const series = displaySeries(node.series, seriesDef.key, dates);
      const row = {
        ...base,
        Показатель: seriesDef.label,
        Метрика: metricDef.label,
      };
      dates.forEach((d) => {
        row[d.label || d.key] = excelNumber(seriesAt(series, d.key, metricDef.key));
      });
      row.Итого = excelNumber(seriesTotal(series, metricDef.key));
      out.push(row);
    });
  });

  (node.children || []).forEach((child) => appendPlanExportRows(out, child, dates, exportMetrics, path));
}

function buildSummarySheetRows({ plan, totals, dates, gran, canFinance, exportMetrics, totalsFactProjectedSeries }) {
  const rows = [
    ['Название плана', plan.name || ''],
    ['Статус', STATUS_LABEL[plan.status] || plan.status || ''],
    ['Режим прогноза', FORECAST_MODE_LABEL[plan.forecast_mode] || 'Простой'],
    ['Гранулярность', GRANULARITY_LABEL[gran] || gran || ''],
    ['Базовый период', `${fmtDate(plan.base_from)} – ${fmtDate(plan.base_to)}`],
    ['Период прогноза', `${fmtDate(plan.plan_from)} – ${fmtDate(plan.plan_to)}`],
    ['Колонок дат', dates.length],
    ['Финансы доступны', canFinance ? 'Да' : 'Нет'],
    ['Сформировано', new Date().toLocaleString('ru-RU')],
    [],
    ['Показатель', 'Метрика', 'Значение'],
  ];

  exportMetrics.forEach((metricDef) => {
    rows.push(['План', metricDef.label, excelNumber(seriesTotal(totals?.series?.plan, metricDef.key))]);
    rows.push(['Факт', metricDef.label, excelNumber(seriesTotal(totalsFactProjectedSeries, metricDef.key))]);
  });

  return rows;
}

function buildMarketplaceExportRows(totals, exportMetrics) {
  const rows = [];
  for (const code of ['1', '2', '3']) {
    const mpName = MP_MAP[code] || code;
    [
      { label: 'План', source: totals?.plan?.byMp?.[code] },
      { label: 'Факт на сегодня', source: totals?.fact?.byMp?.[code] },
    ].forEach(({ label, source }) => {
      exportMetrics.forEach((metricDef) => {
        const value = Number(source?.[metricDef.key]) || 0;
        if (metricDef.key !== 'qty' && !value) return;
        rows.push({
          Маркетплейс: mpName,
          Показатель: label,
          Метрика: metricDef.label,
          Значение: excelNumber(value),
        });
      });
    });
  }
  return rows;
}

export default function SalesPlanView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((s) => s.auth.user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState(null); // null => использовать из плана
  const [expanded, setExpanded] = useState(() => new Set());
  const [metric, setMetric] = useState('qty');
  const [chartMetric, setChartMetric] = useState('qty');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (gran) => {
    setLoading(true);
    try {
      const params = {};
      if (gran) params.granularity = gran;
      const resp = await axios.get(`/analytics/plans/${id}`, { params });
      setData(resp.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(granularity); }, [load, granularity]);

  const canFinance = resolveCanFinance(data?.can_finance, user);
  const metrics = useMemo(() => availableMetrics(canFinance), [canFinance]);

  // Откат на «Штуки», если выбранная денежная метрика недоступна
  useEffect(() => {
    if (!canFinance && isMoneyMetric(metric)) setMetric('qty');
    if (!canFinance && isMoneyMetric(chartMetric)) setChartMetric('qty');
  }, [canFinance, metric, chartMetric]);

  const toggleExpand = (rowId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const handleArchiveToggle = async () => {
    if (!data?.plan) return;
    setBusy(true);
    try {
      const action = data.plan.status === 'archived' ? 'activate' : 'archive';
      await axios.post(`/analytics/plans/${id}/${action}`);
      notify(data.plan.status === 'archived' ? 'План активирован' : 'План отправлен в архив', { severity: 'success' });
      await load(granularity);
    } catch (_) { /* */ } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`/analytics/plans/${id}`);
      notify('План удалён', { severity: 'success' });
      navigate('/analytics/plans');
    } catch (_) { setBusy(false); }
  };

  const handleExportExcel = () => {
    if (!data?.plan) return;
    try {
      const exportMetrics = availableMetrics(canFinance);
      const exportRows = [];
      (rows || []).forEach((node) => appendPlanExportRows(exportRows, node, dates, exportMetrics));

      const wb = XLSX.utils.book_new();
      const planSheet = XLSX.utils.json_to_sheet(exportRows.length ? exportRows : [{ Позиция: 'Нет позиций' }]);
      planSheet['!cols'] = [
        { wch: 14 },
        { wch: 48 },
        { wch: 28 },
        { wch: 16 },
        { wch: 14 },
        ...dates.map(() => ({ wch: 14 })),
        { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, planSheet, 'План');

      const summaryRows = buildSummarySheetRows({
        plan,
        totals,
        dates,
        gran,
        canFinance,
        exportMetrics,
        totalsFactProjectedSeries,
      });
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
      summarySheet['!cols'] = [{ wch: 22 }, { wch: 26 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Сводка');

      const mpRows = buildMarketplaceExportRows(totals, exportMetrics);
      if (mpRows.length) {
        const mpSheet = XLSX.utils.json_to_sheet(mpRows);
        mpSheet['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, mpSheet, 'По МП');
      }

      XLSX.writeFile(wb, `План_${safeFilePart(plan.name)}_${todayYmd()}.xlsx`);
    } catch (e) {
      notify(e?.message || 'Не удалось выгрузить Excel', { severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
    );
  }
  if (!data?.plan) {
    return (
      <>
        <PageHeader title="План не найден" breadcrumbs={[{ label: 'Планы продаж', path: '/analytics/plans' }, { label: 'План' }]} />
        <Button onClick={() => navigate('/analytics/plans')}>← К списку планов</Button>
      </>
    );
  }

  const { plan, totals, rows, timeseries } = data;
  const dates = data.dates || [];
  const gran = data.granularity || plan.granularity;
  const forecastMeta = extractForecastMetadata(data, plan, totals);
  // Колонки: Позиция + Тип строки + даты + Итого
  const colCount = 2 + dates.length + 1;

  // Ячейка-название позиции, объединённая по вертикали на все строки метрик (rowSpan).
  // Рендерится только в первой строке метрик (План). Здесь же — chevron раскрытия.
  const renderNameCell = (node, depth, rowId, hasChildren, isOpen, removed) => (
    <TableCell
      rowSpan={SERIES_ROWS.length}
      sx={{
        pl: 1 + depth * 2.5,
        verticalAlign: 'middle',
        minWidth: 240,
        bgcolor: (t) => t.palette.surfaceContainerLow,
        opacity: removed ? 0.45 : 1,
        borderRight: (t) => `1px solid ${t.palette.outlineVariant}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {hasChildren ? (
          <IconButton size="small" onClick={() => toggleExpand(rowId)} sx={{ p: 0.25 }}>
            {isOpen ? <DownIcon fontSize="small" /> : <RightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: node.level === 'gruppa' ? 600 : node.level === 'podgruppa' ? 500 : 400, textDecoration: removed ? 'line-through' : 'none' }}>
          {node.name || node.key}
        </Typography>
        {node.added && <Chip size="small" label="новая" color="secondary" variant="outlined" sx={{ ml: 1 }} />}
      </Box>
    </TableCell>
  );

  // Строка одного датасета (План/Факт).
  // На первой строке (seriesIdx === 0) слева добавляется ячейка-название с rowSpan.
  const renderSeriesRow = (rowId, node, depth, seriesDef, seriesIdx, ctx) => {
    const series = displaySeries(node.series, seriesDef.key, dates);
    return (
      <TableRow
        key={`${rowId}::${seriesDef.key}`}
        hover
        sx={{
          opacity: ctx.removed ? 0.45 : 1,
          bgcolor: (t) => alpha(t.palette[seriesDef.tone].main, t.palette.mode === 'dark' ? 0.16 : 0.07),
          '&:hover': {
            bgcolor: (t) => alpha(t.palette[seriesDef.tone].main, t.palette.mode === 'dark' ? 0.24 : 0.13),
          },
        }}
      >
        {seriesIdx === 0 && renderNameCell(node, depth, rowId, ctx.hasChildren, ctx.isOpen, ctx.removed)}
        <TableCell sx={{ pl: 2 }}>
          <Typography variant="caption" sx={{ color: seriesDef.color, fontWeight: seriesDef.strong ? 600 : 500 }}>
            {seriesDef.label}
          </Typography>
        </TableCell>
        {dates.map((d) => {
          const projectedCell = seriesDef.key === 'factProjected' && ['forecast', 'mixed'].includes(dateKind(d));
          return (
          <TableCell
            key={d.key}
            align="right"
            sx={{
              fontWeight: seriesDef.strong ? 500 : 400,
              bgcolor: projectedCell ? (t) => alpha(t.palette.warning.main, t.palette.mode === 'dark' ? 0.18 : 0.10) : undefined,
              borderLeft: projectedCell ? (t) => `1px dashed ${alpha(t.palette.warning.main, 0.7)}` : undefined,
            }}
          >
            {fmtMetric(seriesAt(series, d.key, metric), metric)}
          </TableCell>
          );
        })}
        <TableCell align="right" sx={{ fontWeight: 600, borderLeft: (t) => `1px solid ${t.palette.outlineVariant}` }}>
          {fmtMetric(seriesTotal(series, metric), metric)}
        </TableCell>
      </TableRow>
    );
  };

  const renderRows = (nodes, depth, parentRowId) => {
    const out = [];
    for (const node of nodes) {
      const rowId = `${parentRowId}/${node.level}:${node.key}`;
      const hasChildren = node.children && node.children.length > 0;
      const isOpen = expanded.has(rowId);
      const removed = node.removed;
      const ctx = { hasChildren, isOpen, removed };
      // 2 строки метрик; в первой — ячейка-название с rowSpan
      SERIES_ROWS.forEach((sd, idx) => out.push(renderSeriesRow(rowId, node, depth, sd, idx, ctx)));
      // Дочерние позиции — только при раскрытии chevron
      if (isOpen && hasChildren) out.push(...renderRows(node.children, depth + 1, rowId));
    }
    return out;
  };

  const chartMetricsAvail = canFinance ? CHART_METRICS : CHART_METRICS.filter((m) => !m.money);
  const cm = CHART_METRICS.find((m) => m.key === chartMetric) || CHART_METRICS[0];
  const totalsFactProjectedSeries = displaySeries(totals?.series, 'factProjected', dates);
  const chartData = dates.length && totals?.series
    ? dates.map((d) => ({
      key: d.label || d.key,
      План: seriesAt(totals.series.plan, d.key, cm.key),
      Факт: seriesAt(totalsFactProjectedSeries, d.key, cm.key),
    }))
    : (timeseries || []).map((b) => ({
      key: b.key,
      План: b[cm.plan],
      Факт: b[cm.fact],
    }));

  const planTotal = totals?.plan?.total || {};
  const factProjectedTotal = totalsFactProjectedSeries?.total || totals?.fact?.total || {};

  // KPI-карточки План / Факт с будущим прогнозом. `tone` переиспользует палитру строк таблицы и легенды графика.
  const kpiCards = [
    { label: 'План', tone: 'primary', t: planTotal },
    { label: 'Факт + прогноз', tone: 'success', t: factProjectedTotal },
  ];

  // Строки показателей внутри карточки: штуки всегда, денежные — только при canFinance.
  const kpiRows = (t) => {
    const out = [{ label: 'Штуки', value: fmtInt(t.qty) }];
    if (canFinance) {
      out.push({ label: 'Сумма', value: fmtMoney(t.revenue) });
      out.push({ label: 'Профит', value: fmtMoney(t.profit) });
      if (t.roi !== undefined && t.roi !== null) out.push({ label: 'ROI', value: `${fmtInt(t.roi)}%` });
    }
    return out;
  };

  // Цвет заголовка карточки: onContainer в светлой теме, светлый тон — в тёмной (контраст с tonal-фоном).
  const toneTitleColor = (tone) => (th) => (th.palette.mode === 'dark' ? th.palette[tone].light : th.palette[tone].dark);

  return (
    <>
      <PageHeader
        title={plan.name}
        subtitle={`Прогноз: ${fmtDate(plan.plan_from)} – ${fmtDate(plan.plan_to)} · База: ${fmtDate(plan.base_from)} – ${fmtDate(plan.base_to)}`}
        breadcrumbs={[{ label: 'Планы продаж', path: '/analytics/plans' }, { label: plan.name }]}
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={`Прогноз: ${FORECAST_MODE_LABEL[plan.forecast_mode] || 'Простой'}`} color={FORECAST_MODE_COLOR[plan.forecast_mode] || 'default'} variant="outlined" />
            {forecastMeta && (
              <Tooltip title="Технические детали прогноза показываются только если их передал API. Плановые значения не пересчитываются на фронте.">
                <Box>
                  <ForecastMetadataChips metadata={forecastMeta} />
                </Box>
              </Tooltip>
            )}
            <Chip size="small" label={STATUS_LABEL[plan.status] || plan.status} color={STATUS_COLOR[plan.status] || 'default'} variant="outlined" />
            <Button startIcon={<FileDownloadIcon />} onClick={handleExportExcel}>
              Выгрузить Excel
            </Button>
            <Button startIcon={plan.status === 'archived' ? <UnarchiveIcon /> : <ArchiveIcon />} onClick={handleArchiveToggle} disabled={busy}>
              {plan.status === 'archived' ? 'Активировать' : 'В архив'}
            </Button>
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)} disabled={busy}>Удалить</Button>
          </Stack>
        )}
      />

      {/* Сводные карточки: План / Факт с прогнозом */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} md={6} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: 2.5,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                border: (t) => `1px solid ${alpha(t.palette[card.tone].main, 0.3)}`,
                bgcolor: (t) => alpha(t.palette[card.tone].main, t.palette.mode === 'dark' ? 0.16 : 0.08),
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: toneTitleColor(card.tone) }}>
                {card.label}
              </Typography>
              <Stack spacing={1.25} sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
                {kpiRows(card.t).map((r) => (
                  <Box key={r.label} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">{r.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>{r.value}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Разбивка по маркетплейсам (только штуки) */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Разбивка по маркетплейсам</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">План, шт</Typography>
            <Typography variant="body2">{mpBreakdown(totals?.plan?.byMp, 'qty') || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Факт на сегодня, шт</Typography>
            <Typography variant="body2">{mpBreakdown(totals?.fact?.byMp, 'qty') || '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* График план vs факт */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>План vs Факт</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={chartMetric} onChange={(_, v) => v && setChartMetric(v)}>
              {chartMetricsAvail.map((m) => <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={gran} onChange={(_, v) => v && setGranularity(v)}>
              <ToggleButton value="day">День</ToggleButton>
              <ToggleButton value="week">Неделя</ToggleButton>
              <ToggleButton value="month">Месяц</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
              <XAxis dataKey="key" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtInt(v)} />
              <RTooltip formatter={(v) => (cm.money ? fmtMoney(v) : fmtInt(v))} />
              <Legend />
              <Line type="monotone" dataKey="План" stroke="#6750A4" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Факт" stroke="#006E1C" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Таблица «позиции × даты» */}
      <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5, borderBottom: (t) => `1px solid ${t.palette.outlineVariant}` }}>
          <Typography variant="subtitle1" fontWeight={600}>Позиции по датам</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
              {metrics.map((m) => <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={gran} onChange={(_, v) => v && setGranularity(v)}>
              <ToggleButton value="day">День</ToggleButton>
              <ToggleButton value="week">Неделя</ToggleButton>
              <ToggleButton value="month">Месяц</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 240 }}>Позиция</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Показатель</TableCell>
                {dates.map((d) => (
                  <TableCell key={d.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{d.label}</TableCell>
                ))}
                <TableCell align="right" sx={{ borderLeft: (t) => `1px solid ${t.palette.outlineVariant}`, fontWeight: 600 }}>Итого</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!rows || rows.length === 0) && (
                <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>Нет позиций</TableCell></TableRow>
              )}
              {rows && renderRows(rows, 0, 'root')}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Удалить план?</DialogTitle>
        <DialogContent>
          <DialogContentText>План «{plan.name}» и все его позиции будут удалены безвозвратно.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busy}>Удалить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
